import assetActionTypeService from "../../../../services/assetService/assetActionService/assetActionTypeService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_ASSETACTIONTYPES } from "../../../../utils/constants";
import { AssetActionTypeInterface } from "../../../../models/AssetModel/AssetAction/AssetActionType";

interface UpdateAssetActionTypeContext {
  previousAssetActionTypes: AssetActionTypeInterface[];
}

const useUpdateAssetActionType = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    AssetActionTypeInterface,
    Error,
    AssetActionTypeInterface,
    UpdateAssetActionTypeContext
  >({
    mutationFn: async (assetActionType: AssetActionTypeInterface) => {
      // Assuming the ID is stored in the assetActionType object or can be derived
      const { id, ...assetActionTypeData } = assetActionType;
      return assetActionTypeService.Update(id || "", assetActionTypeData);
    },

    onMutate: async (updatedAssetActionType: AssetActionTypeInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_ASSETACTIONTYPES],
      });

      const previousAssetActionTypes =
        queryClient.getQueryData<AssetActionTypeInterface[]>([
          CACHE_KEY_ASSETACTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<AssetActionTypeInterface[]>(
        [
          CACHE_KEY_ASSETACTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (assetActionTypes = []) =>
          assetActionTypes.map((assetActionType) =>
            assetActionType.id === updatedAssetActionType.id
              ? updatedAssetActionType
              : assetActionType
          )
      );

      return { previousAssetActionTypes };
    },

    onSuccess: (savedAssetActionType) => {
      queryClient.setQueryData<AssetActionTypeInterface[]>(
        [
          CACHE_KEY_ASSETACTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (assetActionTypes) =>
          assetActionTypes?.map((assetActionType) =>
            assetActionType.id === savedAssetActionType.id
              ? savedAssetActionType
              : assetActionType
          )
      );
    },

    onError: (error, updatedAssetActionType, context) => {
      if (!context) return;

      queryClient.setQueryData<AssetActionTypeInterface[]>(
        [
          CACHE_KEY_ASSETACTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousAssetActionTypes
      );
    },
  });
};

export default useUpdateAssetActionType;
